import React, { useCallback, useState } from "react";
import { Button, Flex, Text } from "@jsq/ds/core";
import { Colors, Sizes } from "@jsq/ds/styles";
import {
  type DetailedPaymentPrefUnionObjectType,
  type RedactedPaymentPref,
} from "../../common/types";
import * as styles from "./styles_vanilla.css";
import { getPaymentMethodText, getTitle } from "./utils";

export type BasePaymentPrefDisplayProps = {
  /** Payment preference data to display */
  paymentPref: DetailedPaymentPrefUnionObjectType | RedactedPaymentPref;
  /** Additional content to render below the payment details */
  children?: React.ReactNode;
  /** Whether to show the reveal/hide details button */
  displayRevealDetails?: boolean;
  /** Function that renders the detailed payment information */
  renderDetails: () => JSX.Element;
  /** Whether details should be expanded by default */
  defaultExpanded?: boolean;
  /** Optional accessory content (string or component) displayed between title and reveal button */
  accessory?: React.ReactNode;
  /** Whether the card should take up the full width */
  isFullWidth?: boolean;
};

/**
 * Base component for displaying payment preference information with optional accessory content.
 *
 * This component renders payment details including title, payment method, and optional reveal/hide
 * functionality. It supports displaying accessory content (like tags or badges) between the title
 * and reveal details button.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <BasePaymentPrefDisplay
 *   paymentPref={paymentPref}
 *   renderDetails={() => <PaymentDetails />}
 * />
 *
 * // With accessory content
 * <BasePaymentPrefDisplay
 *   paymentPref={paymentPref}
 *   renderDetails={() => <PaymentDetails />}
 *   accessory={<Tag variant="warning">Pending Review</Tag>}
 *   displayRevealDetails
 * />
 *
 * // With string accessory
 * <BasePaymentPrefDisplay
 *   paymentPref={paymentPref}
 *   renderDetails={() => <PaymentDetails />}
 *   accessory="auto-generated"
 * />
 * ```
 */
export const BasePaymentPrefDisplay: React.FC<BasePaymentPrefDisplayProps> = ({
  paymentPref,
  children,
  displayRevealDetails,
  renderDetails,
  defaultExpanded,
  accessory,
  isFullWidth,
}: BasePaymentPrefDisplayProps) => {
  const [revealDetails, setRevealDetails] = useState(defaultExpanded ?? false);
  const paymentMethodText = getPaymentMethodText(paymentPref);
  const title = getTitle(paymentPref);

  const handleRevealDetailsClick = useCallback(() => {
    setRevealDetails((revealDetails) => !revealDetails);
  }, []);

  return (
    <Flex
      className={isFullWidth ? styles.paymentBlockFullWidth : styles.paymentBlock}
      flexDirection="column"
    >
      <Flex flexDirection="column">
        <Flex justifyContent="flex-start" alignItems="center" flexDirection="row">
          <Text className={styles.title} variant="strong-md">
            {title}
          </Text>
          {accessory && (
            <Flex alignItems="center" gap={8} style={{ marginLeft: Sizes.MD }}>
              {accessory}
            </Flex>
          )}
          {displayRevealDetails && (
            <Button
              icon={revealDetails ? "eye-slash" : "eye"}
              iconVariant="solid"
              variant="link"
              displayAsLink
              className={styles.revealDetailsButton}
              onClick={handleRevealDetailsClick}
              data-testid="reveal-details-button"
            >
              {revealDetails ? "Hide details" : "Reveal details"}
            </Button>
          )}
        </Flex>
        <Flex key="payment-method">
          <Text variant="strong-sm" color={Colors.Neutral900}>
            {paymentMethodText}
          </Text>
        </Flex>
      </Flex>
      {revealDetails && renderDetails()}
      {children}
    </Flex>
  );
};
